/**
 * Módulo 7 (cierra el ciclo) del tutorial: Los números del campo (KAN-232).
 *
 * Gastos y compras con el formulario unificado: concepto del catálogo (sin
 * listas viejas), el pivot Consumo Inmediato vs Inventario, el destino del
 * gasto (General/Engorde, con la categoría Alimentación como caso especial y
 * sus valores nutricionales) y el corral opcional para engorde (costo directo
 * o prorrateo). Alta desde el Panel principal y desde Administración, y
 * cierre en el Libro Diario.
 *
 * Anclas: menu-administracion, menu-quick-gasto (menú lateral; admin-quick-gasto
 * como avance alternativo), gasto-form-concepto/detalle/monto/asignacion/
 * es-engorde/corral, btn-save-transaction, admin-libro-diario.
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';
import { getRepositories } from '../../../services/storage';

const ADMIN = '/administracion';

export const administracionModule: TutorialModuleDef = {
  id: 'administracion',
  title: 'Los números del campo',
  description: 'Gastos y compras bien cargados: consumo inmediato o inventario, destino del gasto y hasta a qué corral le cargás el costo.',
  emoji: '💰',
  image: 'images/tutorial/administracion.png',
  permission: { module: 'financial', action: 'create' },
  grupo: 'esencial',
  route: ADMIN,
  steps: [
    {
      id: 'concepto-numeros',
      route: '/tutorial',
      text: 'El campo también es una caja: lo que entra, lo que sale — y cada gasto bien cargado te dice después qué te cuesta producir.',
      narration:
        'Último trecho del arranque, y es el que decide si el año cierra redondo: los números. El campo también es una caja: entra plata cuando vendés, sale cuando pagás. Y lo importante no es sólo anotarlo, sino cargarlo bien: qué era, si se consumió o quedó en el inventario, y si era para la hacienda. Con eso, la app te dice después qué te cuesta producir. Empecemos.',
    },
    {
      id: 'ir-a-administracion',
      target: '[data-ar-guide="menu-administracion"]',
      label: 'Administración (menú Finanzas)',
      text: 'En el menú, sección Finanzas, tocá "Administración" (en celular: menú ☰).',
      narration:
        'Ahí tenés el tablero completo: gastos, ingresos, libro diario e inventario. Entrá nomás, que apenas llegás te hago un paneo del terreno y te muestro un atajo que queda siempre a mano.',
    },
    {
      id: 'paneo-administracion',
      route: ADMIN,
      target: '[data-ar-guide="admin-quick-actions"]',
      extraTargets: [
        '[data-ar-guide="admin-quick-consumo"]',
        '[data-ar-guide="admin-quick-venta"]',
        '[data-ar-guide="admin-mod-resultados"]',
        '[data-ar-guide="admin-libro-diario"]',
        '[data-ar-guide="admin-stocks"]',
        '[data-ar-guide="admin-gastos"]',
      ],
      label: 'Paneo: 4 acciones + los módulos',
      advanceOnClick: false,
      text: 'Antes de meternos, un paneo: ARRIBA tenés las 4 acciones rápidas (Cargar Ingreso, Cargar Gasto/Compra, Usar Inventario, Vender Inventario) y ABAJO los módulos: Resultados, Libro Diario, Inventarios, Gastos, Conceptos y la Valuación de tu ganado. Hoy entramos por Gasto.',
      narration:
        'Mirá la pantalla un segundo antes de tocar nada, que es la comand central de los números. Arriba, las cuatro acciones rápidas, las del día a día: cargar un ingreso, cargar un gasto o compra, usar inventario cuando consumís algo del galpón, y vender inventario cuando sale producto. Y abajo, los módulos para mirar: el estado de resultados de fin de año, el libro diario con todo el movimiento, los inventarios de lo que tenés parado, el gasto fino por categoría, los conceptos que se van aprendiendo de tus cargas, y la valuación de tu ganado a precio de mercado. Cargar arriba, mirar abajo. Hoy vamos a entrar por la de Gasto, que es la que más vas a usar.',
    },

    {
      id: 'alta-rapida-gasto',
      route: ADMIN,
      target: '[data-ar-guide="menu-quick-gasto"]',
      advanceTargets: ['[data-ar-guide="admin-quick-gasto"]'],
      label: 'Cargar Gasto/Compra (menú lateral o Administración)',
      text: 'El formulario se abre de dos formas: "Cargar Gasto/Compra" en el MENÚ LATERAL (te lo señalo — está siempre a mano) o el "Cargar Gasto" de acá de Administración. Tocá CUALQUIERA de los dos.',
      narration:
        'El mejor momento para anotar un gasto es cuando lo pagás, no en diciembre. Y para eso tenés dos puertas: la del menú lateral, que está siempre a mano en cualquier pantalla, ésta que te señalo; o la de acá mismo en Administración. Las dos abren el mismo formulario. Tocá la que quieras: la factura en una mano, el dedo en la otra, y chau olvido.',
    },
    {
      id: 'gasto-concepto-monto',
      target: '[data-ar-guide="gasto-form-concepto"]',
      label: 'Concepto',
      advanceOnClick: false,
      text: 'Elegí el concepto de la lista — viene ordenadito por categoría: Sanidad, Combustible, Alimentación… Si no existe, escribilo y tocá "+ Crear". Poné el monto al lado.',
      narration:
        'El formulario es a la altura del trabajador. El concepto lo elegís de esta lista, que viene ordenada por categoría: sanidad, combustible, alimentación, lo que sea. Todo en su cajoncito. Y si el concepto no existe todavía, lo escribís y le das a Crear: la app te pregunta a qué categoría va y listo. El monto va al lado, y si querés dejás un detalle para acordarte después.',
    },
    {
      id: 'gasto-asignacion',
      target: '[data-ar-guide="gasto-form-asignacion"]',
      label: 'Consumo Inmediato vs Inventario',
      advanceOnClick: false,
      text: '"Asignar a" es la gran decisión: 💸 CONSUMO INMEDIATO (ya se usó — gasoil, viáticos: va directo a gastos) o 📦 INVENTARIO (queda guardado — postes, alimento: entra al stock y recién impacta cuando lo consumís).',
      narration:
        'Ahora la decisión importante del formulario: asignar a qué. Consumo inmediato es cuando ya se usó: el gasoil del día, los viáticos. Va derecho a gastos, sin vuelta. Inventario es cuando queda guardado: postes, alambre, alimento. Eso entra al stock con su cantidad, y recién impacta en los números cuando lo vas consumiendo. ¿La prueba del algodón? Si lo tenés parado en el galpón, es inventario; si ya se quemó, consumo inmediato.',
    },
    {
      id: 'elegir-alimento',
      target: '[data-ar-guide="gasto-form-concepto"]',
      label: 'Elegí "Maíz"',
      advanceOnClick: false,
      advanceWhenVisible: '[data-ar-guide="modal-nutricion"]',
      text: 'Primero elegí un concepto, porque hasta no elegirlo el formulario no muestra todo: escribí "Maíz" y elegilo de la lista — es un alimento del catálogo. Si ya lo usaste, probá con otro que no hayas usado jamás.',
      narration:
        'Manos a la obra, y arrancamos por el concepto, porque hasta no elegir uno el formulario no muestra todas sus partes. En el campo de concepto escribí maíz, como el grano, y elegilo de la lista de sugeridos. Es un alimento del catálogo, de fábrica. Cuando lo elegís, si es la primera vez que lo usás, se abre solo un cuadro preguntando por sus valores, y de eso seguimos hablando. Aviso para el que ya anduvo tocando: si el maíz ya lo cargaste, probá con otro alimento que no hayas usado nunca.',
    },
    {
      id: 'dialogo-nutricional',
      target: '[data-ar-guide="modal-nutricion"]',
      label: 'Valores del alimento',
      advanceOnClick: false,
      text: '¿Viste qué apareció? Primera vez que usás el alimento: te pregunta sus valores. 💪 PB = Proteína Bruta (forma músculo: ternero ~15%, engorde ~12%). ⚡ EM = Energía Metabolizable en Mcal/kg (la que engorda: objetivo 2,7–3,0). Están en la ETIQUETA de la bolsa. Si no apareció, es que ya lo habías confirmado: pasá de largo.',
      narration:
        'Se abrió solo, ¿viste? La primera vez que usás un alimento, la app te pregunta sus valores, y te conviene saber leerlos. P B es la proteína bruta: la que forma músculo. Un ternero necesita cerca del quince por ciento, la recría trece, el engorde doce. E M es la energía metabolizable, en megacalorías por kilo: la que engorda de verdad. Un buen alimento de engorde anda entre dos siete y tres cero. ¿De dónde salen? De la etiqueta de la bolsa, criollo: están escritos ahí, y con el tiempo los vas a conocer de memoria.',
    },
    {
      id: 'confirmar-alimento',
      target: '[data-ar-guide="modal-nutricion-confirmar"]',
      label: 'Confirmar valores',
      text: 'Si los valores están bien, tocá "Confirmar" — no vuelve a preguntar por este alimento. Si los tuyos son otros, "Editar valores" y guardás tu versión.',
      narration:
        'Si los valores te cierran, tocá confirmar y listo: la app se acuerda y no te vuelve a molestar con este alimento. ¿Los tuyos son otros porque comprás otro maíz? Editar valores, los corregís, y queda guardada tu versión. Confirmá y seguimos.',
    },
    {
      id: 'gasto-es-engorde',
      target: '[data-ar-guide="gasto-form-es-engorde"]',
      label: 'Destino: General o Engorde',
      advanceOnClick: false,
      text: '"Destino del gasto": 🐮 Engorde entra al costo de producción de la hacienda; 🧭 General no. Ya con el Maíz elegido lo ves: la categoría Alimentación viene marcada Engorde SOLA — es el alimento de los animales.',
      narration:
        'Ahora sí, con el concepto elegido, mirá esta parte del formulario: el destino del gasto. Si es para la hacienda, va a Engorde: entra al costo de producción, y cuando vendas sabrás de verdad cuánto ganaste. Si no tiene nada que ver, General. ¿Y ves que con el maíz ya viene marcado Engorde solo? Los conceptos de la categoría Alimentación vienen así de fábrica, porque son el alimento de los animales, es su naturaleza. Vos lo podés cambiar si no corresponde, pero casi nunca corresponde.',
    },
    {
      id: 'crear-alimento-nuevo',
      text: '¿Un alimento que NO está? Escribilo en Concepto y tocá "+ Crear": elegís categoría Alimentación y te pide PB y EM ahí mismo. Después podés corregirlos desde el módulo Conceptos — los incompletos salen con el cartelito "sin nutrición".',
      narration:
        'Y ¿si mañana cargás un alimento que no está en el catálogo? Nada: lo escribís en concepto, tocás crear, le ponés la categoría alimentación, y ahí mismo te pide la proteína y la energía, si las sabés. ¿No las sabés hoy? Crealo igual: después las completás desde el módulo de conceptos, donde los incompletos avisan con un cartelito sin nutrición. La etiqueta de la bolsa, siempre, es tu fuente.',
    },
    {
      id: 'gasto-corral',
      target: '[data-ar-guide="gasto-form-corral"]',
      label: 'Corral (opcional)',
      advanceOnClick: false,
      text: 'Si es de engorde, podés (opcional) elegir un CORRAL: el costo va entero a ese corral. Si lo dejás vacío, se reparte entre todos según los kilos que ganó cada uno.',
      narration:
        'Y el detalle fino para los que hacen engorde: este campo de corral, que es opcional. Si un gasto es de un corral puntual, la ración de ese corral, elegí el corral acá y el costo va entero a él, bien clavado. Si lo dejás vacío, no pasa nada: el gasto se reparte entre todos los corrales, parejito según los kilos que fue ganando cada uno. Tu cuenta de engorde queda fina sin que tengas que hacer nada.',
    },
    {
      id: 'guardar-gasto',
      target: '[data-ar-guide="btn-save-transaction"]',
      label: 'Guardar',
      text: 'Guardá — quedó asentado con su concepto, su destino y su corral. Los ingresos son más simples todavía: monto y concepto, y a la caja.',
      narration:
        'Revisá y tocá guardar: el gasto queda asentado con su concepto, su destino y su corral si le pusiste. Y los ingresos son todavía más fáciles: monto y concepto, y a la caja. Venta de cueros, un ternero de más: cinco segundos y quedó.',
    },
    {
      id: 'libro-diario',
      route: `${ADMIN}/libro-diario`,
      target: '[data-ar-guide="admin-libro-diario"]',
      label: 'Libro Diario',
      text: 'El Libro Diario te lo muestra todo junto: entradas, salidas y el saldo, mes a mes. Quedan dos módulos más y cerramos.',
      narration:
        'Y para mirar todo junto, el Libro Diario: entradas, salidas y el saldo, mes a mes. La foto clara de cómo viene la caja. Antes de cerrar, dos módulos más que te van a servir: el inventario y el estado de resultados.',
    },
    {
      id: 'inventario-meses',
      route: ADMIN,
      target: '[data-ar-guide="admin-stocks"]',
      label: '📦 Inventarios',
      advanceOnClick: false,
      text: 'El módulo INVENTARIOS: todo lo que tenés parado en el galpón. Y lo mejor: cuando cargás un tiempo tus usos, te ESTIMA cuántos meses de inventario te quedan a tu ritmo actual — para comprar antes de quedarte corto.',
      narration:
        'Volviendo a la página principal de Administración, te marco el módulo de Inventarios: todo lo que tenés parado, alimento, sanidad, combustible. Y acá viene la perlita: cuando llevás un tiempo cargando lo que usás, la app te calcula cuántos meses de cada cosa te quedan a tu ritmo de uso actual. El gráfico te pinta en rojo lo que tiene menos de tres meses, para que compres a tiempo y no te agarre el invierno sin alimento.',
    },
    {
      id: 'entrar-resultados',
      route: ADMIN,
      target: '[data-ar-guide="admin-mod-resultados"]',
      label: '📊 Estado de Resultados',
      text: 'El último: el ESTADO DE RESULTADOS — tocálo y entrás, que ahí te explico el caso de una venta.',
      narration:
        'Y el módulo que le pone el broche: el Estado de Resultados. Tocalo y entrá conmigo, que ahí te muestro cómo se calcula la ganancia de verdad.',
    },
    {
      id: 'resultados-ganancia',
      route: `${ADMIN}/resultados`,
      target: '[data-ar-guide="resultados-contenido"]',
      label: 'Cómo se arma la ganancia',
      advanceOnClick: false,
      text: 'Un CASO concreto: vendés animales → la venta entra como INGRESO. De ahí la app resta los COSTOS de producción que venís cargando (engorde, sanidad, alimento…) → lo que queda es la GANANCIA REAL del año, calculada sola.',
      narration:
        'Mirá cómo se arma la película, con un caso concreto. Vendés unos terneros: esa venta entra acá como ingreso. Del otro lado están los costos de producción que venís cargando todo el año con cada gasto: el alimento que marcamos Engorde, la sanidad, todo lo que le sumaste a la hacienda. La app los resta, y lo que queda es la ganancia real, no la de fantasía: sabés de verdad cuánto te dejó cada animal vendido. Por eso cargábamos el destino de cada gasto: sin eso, este número no saldría.',
    },
    {
      id: 'cierre-numeros',
      route: `${ADMIN}/resultados`,
      text: 'Con esto ya recorriste TODO el tutorial: el campo, los animales, el mapa, la declaración, el comercio y los números. ¡Ya sos de la casa, criollo!',
      narration:
        'Con esto ya recorriste todo el tutorial, criollo: el campo, los animales, el mapa, la declaración, el comercio y los números. Ya sos de la casa. ¡Que el campo te rinda, y nos vemos!',
    },
  ],
  completionCheck: async () => {
    const [gastos, ingresos] = await Promise.all([
      getRepositories().gasto.getAllForActiveEmpresa() as Promise<unknown[]>,
      getRepositories().ingreso.getAllForActiveEmpresa() as Promise<unknown[]>,
    ]);
    return gastos.length > 0 || ingresos.length > 0;
  },
};
